
window.TwoLaneQuickView = (function () {

  let modal, scrim, body;
  let lastFocused = null;
  let current = null;         /* normalised product being previewed */
  let selected = [];          /* chosen option values */
  let reqSeq = 0;             /* bumped by every open and close: only the newest read may act */
  let reqAbort = null;        /* AbortController of the pending product read */
  let busyTrigger = null;     /* control marked aria-busy while that read is pending */

  const LIVE = typeof window.Shopify === 'object' && !!window.Shopify.shop;

  function str(key, fallback, vars) {
    return window.t ? window.t(key, fallback, vars) : fallback;
  }

  function sameImage(a, b) {
    return String(a || '').replace(/^https?:/, '') === String(b || '').replace(/^https?:/, '');
  }

  function money(cents) {
    return window.TwoLaneCart
      ? window.TwoLaneCart.formatMoney(cents)
      : '$' + (Number(cents) / 100).toFixed(2);
  }

  function parseMoney(text) {
    const n = parseFloat(String(text).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fromShopify(p, card) {
    const backorder = card && card.dataset.backorderVariants;
    const blocked = card && card.dataset.blockedVariants
      ? card.dataset.blockedVariants.split(',').filter(Boolean) : [];
    return {
      id:       p.id,
      title:    p.title,
      vendor:   p.vendor,
      brandUrl: (card && card.dataset.brandUrl) || '',
      backorder: backorder ? backorder.split(',') : [],
      url:      p.url,
      desc:     (p.description || '').replace(/<[^>]*>/g, '').slice(0, 260),
      images:   p.images && p.images.length ? p.images : [p.featured_image],
      options:  p.options.map((name, i) => ({
        name: typeof name === 'string' ? name : name.name,
        values: Array.from(new Set(p.variants.map(v => v.options[i])))
      })),
      variants: p.variants.map(v => ({
        id: v.id,
        title: v.title,
        sku: v.sku || '',
        options: v.options,
        price: v.price,
        compare_at_price: v.compare_at_price,
        available: v.available && blocked.indexOf(String(v.id)) === -1,
        image: v.featured_image ? v.featured_image.src : null
      })),
      rating: null
    };
  }

  function fromCard(card) {
    const nameEl   = card.querySelector('.product-card__name');
    const priceEl  = card.querySelector('.product-card__price strong');
    const imgEl    = card.querySelector('.product-card__img');
    const ratingEl = card.querySelector('.product-card__count');
    const linkEl   = card.querySelector('a[href]');

    const price = parseMoney(priceEl && priceEl.textContent);

    const swatches = Array.from(card.querySelectorAll('.product-swatch[data-img]'))
      .filter((el, i, arr) => arr.findIndex(x => x.dataset.img === el.dataset.img) === i);

    const mainImage = imgEl ? imgEl.getAttribute('src') : '';
    const images    = [mainImage].concat(swatches.map(s => s.dataset.img)).filter(Boolean);

    const options = swatches.length
      ? [{
          name: 'Color',
          values: swatches.map(s => s.getAttribute('aria-label') || 'Option'),
          swatches: swatches.map(s => ({
            label: s.getAttribute('aria-label') || 'Option',
            thumb: s.querySelector('img') ? s.querySelector('img').src : '',
            full:  s.dataset.img
          }))
        }]
      : [];

    const values = options.length ? options[0].values : ['Default Title'];

    return {
      id:      card.dataset.productId || (nameEl ? nameEl.textContent.trim() : 'item'),
      title:   nameEl ? nameEl.textContent.trim() : 'Product',
      vendor:  '',
      brandUrl: '',
      backorder: [],
      url:     linkEl ? linkEl.getAttribute('href') : 'product.html',
      desc:    '',
      images:  images,
      options: options,
      variants: values.map((v, i) => ({
        id: (card.dataset.productId || 'item') + '-' + i,
        title: v,
        options: [v],
        price: price,
        compare_at_price: null,
        available: true,
        image: options.length ? options[0].swatches[i].full : mainImage
      })),
      rating: ratingEl ? ratingEl.textContent.trim() : null
    };
  }

  function matchVariant() {
    if (!current) return null;
    return current.variants.find(v =>
      v.options.every((opt, i) => selected[i] === undefined || selected[i] === opt)
    ) || null;
  }

  function isValueAvailable(optionIndex, value) {
    return current.variants.some(v => {
      if (v.options[optionIndex] !== value) return false;
      if (!v.available) return false;
      return v.options.every((opt, i) =>
        i === optionIndex || selected[i] === undefined || selected[i] === opt
      );
    });
  }

  function valueExists(optionIndex, value) {
    return current.variants.some(v => {
      if (v.options[optionIndex] !== value) return false;
      return v.options.every((opt, i) =>
        i === optionIndex || selected[i] === undefined || selected[i] === opt
      );
    });
  }

  function syncAvailability() {
    const soldOut     = str('sold_out', 'Sold Out');
    const unavailable = str('unavailable', 'Unavailable');
    body.querySelectorAll('[data-option-index]').forEach(group => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('[data-option-value]').forEach(btn => {
        const value = btn.dataset.optionValue;
        const ok = isValueAvailable(index, value);
        btn.classList.toggle('is-unavailable', !ok);
        btn.setAttribute('aria-label', ok
          ? value
          : value + ' — ' + (valueExists(index, value) ? soldOut : unavailable));
      });
    });
  }

  function optionHtml(option, index) {
    const chosen = selected[index];

    if (option.swatches) {
      return `
        <div class="product-option" data-option-index="${index}">
          <p class="product-option__label">${esc(option.name)}: <span data-option-selected>${esc(chosen)}</span></p>
          <div class="product-option__values">
            ${option.swatches.map(s => `
              <button type="button"
                      class="option-swatch${s.label === chosen ? ' is-selected' : ''}"
                      data-option-value="${esc(s.label)}"
                      data-full="${esc(s.full)}"
                      aria-label="${esc(s.label)}">
                <img src="${esc(s.thumb)}" alt="" />
              </button>`).join('')}
          </div>
        </div>`;
    }

    return `
      <div class="product-option" data-option-index="${index}">
        <p class="product-option__label">${esc(option.name)}: <span data-option-selected>${esc(chosen)}</span></p>
        <div class="product-option__values">
          ${option.values.map(v => `
            <button type="button"
                    class="option-pill${v === chosen ? ' is-selected' : ''}"
                    data-option-value="${esc(v)}">${esc(v)}</button>`).join('')}
        </div>
      </div>`;
  }

  function render() {
    const initial = current.variants.find(v => v.available) || current.variants[0];
    selected = current.options.map(function (o, i) {
      return initial && initial.options[i] !== undefined ? initial.options[i] : o.values[0];
    });

    const ratingHtml = current.rating
      ? `<div class="product-rating">
           <span class="product-rating__stars"><img src="${(window.themeAssets && window.themeAssets.star) || 'assets/svg/star.svg'}" alt="" /></span>
           <span class="product-rating__count">${esc(current.rating)} reviews</span>
         </div>`
      : '';

    const brandHref = current.brandUrl || '/collections/vendors?q=' + encodeURIComponent(current.vendor || '');

    body.innerHTML = `
      <div class="qv__media">
        <div class="qv__stage">
          <img src="${esc(current.images[0])}" alt="${esc(current.title)}" data-qv-main />
        </div>
        ${current.images.length > 1 ? `
        <div class="qv__thumbs">
          ${current.images.map((src, i) => `
            <button type="button" class="qv__thumb${i === 0 ? ' is-active' : ''}"
                    data-qv-thumb="${esc(src)}"
                    aria-label="${esc(str('view_image', 'View image ' + (i + 1), { INDEX: i + 1 }))}"
                    aria-pressed="${i === 0 ? 'true' : 'false'}">
              <img src="${esc(src)}" alt="" />
            </button>`).join('')}
        </div>` : ''}
      </div>

      <div class="qv__info" data-product-form>
        ${current.vendor ? `<p class="product-info__brand"><a href="${esc(brandHref)}">${esc(current.vendor)}</a></p>` : ''}
        <h2 class="qv__title">${esc(current.title)}</h2>

        <div class="product-info__meta">
          ${ratingHtml}
          <span class="product-info__sku" data-qv-sku></span>
        </div>

        <div class="product-price">
          <span class="product-price__current" data-qv-price></span>
          <span class="product-price__compare" data-qv-compare hidden></span>
        </div>

        <p class="product-stock" data-qv-stock></p>

        ${current.options.map(optionHtml).join('')}

        <div class="product-buy">
          <div class="qty">
            <button type="button" class="qty__btn" data-qty-minus aria-label="Decrease quantity">&minus;</button>
            <input class="qty__input" type="number" min="1" value="1" data-product-qty aria-label="Quantity" />
            <button type="button" class="qty__btn" data-qty-plus aria-label="Increase quantity">+</button>
          </div>
          <button type="button"
                  class="product-buy__add"
                  data-add-to-cart
                  data-title="${esc(current.title)}"
                  data-url="${esc(current.url)}"></button>
        </div>

        ${current.desc ? `<p class="qv__desc">${esc(current.desc)}</p>` : ''}

        <a class="qv__full-link" href="${esc(current.url)}">
          ${esc(str('view_details', 'View full details'))}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </a>
      </div>`;

    refreshBuyState();
  }

  function refreshBuyState() {
    const variant = matchVariant();
    const priceEl = body.querySelector('[data-qv-price]');
    const compEl  = body.querySelector('[data-qv-compare]');
    const addBtn  = body.querySelector('[data-add-to-cart]');
    const mainImg = body.querySelector('[data-qv-main]');
    const skuEl   = body.querySelector('[data-qv-sku]');
    const stockEl = body.querySelector('[data-qv-stock]');

    syncAvailability();

    if (!variant) {
      if (priceEl) priceEl.textContent = '';
      if (compEl)  compEl.hidden = true;
      if (skuEl)   skuEl.textContent = '';
      if (stockEl) { stockEl.textContent = ''; stockEl.hidden = true; }
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = str('unavailable', 'Unavailable');
        addBtn.classList.remove('is-sold-out');
        delete addBtn.dataset.id;
        delete addBtn.dataset.variantId;
      }
      return;
    }

    if (priceEl) priceEl.textContent = money(variant.price);

    if (compEl) {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      compEl.hidden = !hasCompare;
      compEl.textContent = hasCompare ? money(variant.compare_at_price) : '';
    }

    if (skuEl) skuEl.textContent = variant.sku ? str('sku', 'SKU') + ': ' + variant.sku : '';

    if (stockEl) {
      const backorder = variant.available && current.backorder.indexOf(String(variant.id)) !== -1;
      stockEl.hidden = backorder;
      stockEl.textContent = !variant.available
        ? str('sold_out', 'Sold Out')
        : (backorder ? '' : str('in_stock', 'In stock'));
      stockEl.classList.toggle('product-stock--out', !variant.available);
    }

    if (variant.image && mainImg) {
      mainImg.src = variant.image;
      const thumbs = Array.from(body.querySelectorAll('[data-qv-thumb]'));
      const match = thumbs.find(t => sameImage(t.dataset.qvThumb, variant.image));
      if (match) {
        thumbs.forEach(t => {
          t.classList.toggle('is-active', t === match);
          t.setAttribute('aria-pressed', t === match ? 'true' : 'false');
        });
      }
    }

    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? str('add_to_cart', 'Add to Cart')
        : str('sold_out', 'Sold Out');
      addBtn.classList.toggle('is-sold-out', !variant.available);
      addBtn.dataset.id           = variant.id;
      addBtn.dataset.variantId    = variant.id;
      addBtn.dataset.price        = variant.price;
      addBtn.dataset.variantTitle = variant.title === 'Default Title' ? '' : variant.title;
      addBtn.dataset.image        = variant.image || current.images[0];
    }
  }

  function show(trigger) {
    if (!modal.classList.contains('is-open')) {
      const active = document.activeElement;
      lastFocused = active && active !== document.body ? active : (trigger || active);
    }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    scrim.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('has-overlay');
    modal.querySelector('[data-qv-close]').focus();
  }

  function setBusy(el) {
    if (busyTrigger && busyTrigger !== el) busyTrigger.removeAttribute('aria-busy');
    busyTrigger = el || null;
    if (busyTrigger) busyTrigger.setAttribute('aria-busy', 'true');
  }

  function cancelPending() {
    reqSeq++;
    if (reqAbort) { reqAbort.abort(); reqAbort = null; }
    setBusy(null);
  }

  function close() {
    cancelPending();
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    scrim.classList.remove('is-active');
    document.body.style.overflow = '';
    document.body.classList.remove('has-overlay');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function openFromCard(card, trigger) {
    const handle = card.dataset.productHandle;

    cancelPending();
    const req = reqSeq;

    if (LIVE && handle) {
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      reqAbort = ctrl;
      setBusy(trigger);
      fetch('/products/' + handle + '.js', ctrl ? { signal: ctrl.signal } : undefined)
        .then(r => {
          if (!r.ok) throw new Error('Product request failed: ' + r.status);
          return r.json();
        })
        .then(p => {
          if (req !== reqSeq) return;
          reqAbort = null;
          setBusy(null);
          current = fromShopify(p, card); render(); show(trigger);
        })
        .catch(() => {
          if (req !== reqSeq) return;
          reqAbort = null;
          setBusy(null);
          const link = card.dataset.url || (card.querySelector('a[href]') || {}).href;
          if (link) window.location.href = link;
        });
      return;
    }

    current = fromCard(card);
    render();
    show(trigger);
  }

  function init() {
    modal = document.getElementById('quick-view');
    scrim = document.getElementById('qv-scrim');
    if (!modal || !scrim) return;
    body = modal.querySelector('[data-qv-body]');

    document.addEventListener('click', function (e) {

      const trigger = e.target.closest('.product-card__choose, [data-quick-view]');
      if (trigger) {
        const card = trigger.closest('.product-card') || trigger.closest('[data-product-card]');
        if (card) { e.preventDefault(); openFromCard(card, trigger); return; }
      }

      if (e.target.closest('[data-qv-close]') || e.target.closest('.qv-scrim')) {
        close();
        return;
      }

      if (!modal.classList.contains('is-open')) return;

      const thumb = e.target.closest('[data-qv-thumb]');
      if (thumb) {
        const main = body.querySelector('[data-qv-main]');
        main.removeAttribute('srcset');
        main.src = thumb.dataset.qvThumb;
        body.querySelectorAll('[data-qv-thumb]').forEach(t => {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');
        return;
      }

      const optionBtn = e.target.closest('[data-option-value]');
      if (optionBtn && modal.contains(optionBtn)) {
        const group = optionBtn.closest('[data-option-index]');
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-option-value]').forEach(b => b.classList.remove('is-selected'));
        optionBtn.classList.add('is-selected');
        selected[index] = optionBtn.dataset.optionValue;
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = optionBtn.dataset.optionValue;
        refreshBuyState();
        return;
      }

      if (modal.contains(e.target)) {
        const qty = body.querySelector('[data-product-qty]');
        if (qty && e.target.closest('[data-qty-plus]'))  qty.value = Number(qty.value) + 1;
        if (qty && e.target.closest('[data-qty-minus]')) qty.value = Math.max(1, Number(qty.value) - 1);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (modal.classList.contains('is-open')) close();
      else cancelPending();
    });

    if (window.TwoLaneA11y) {
      document.addEventListener('keydown', window.TwoLaneA11y.trap(modal, function () {
        return modal.classList.contains('is-open');
      }));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { close: close, openFromCard: openFromCard };

})();
